import { useEffect, useMemo, useState } from "react";
import api from "../api";
import { fmtCLP } from "../utils/formato";
import toast from "../utils/toast";
import "./ProductManager.css";

/* --------------------- Modal reutilizable --------------------- */
function Modal({ open, onClose, children, title }) {
  if (!open) return null;
  return (
    <div className="pm-modal-backdrop" onMouseDown={onClose}>
      <div
        className="pm-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="pm-modal__head">
          <h3>{title}</h3>
          <button className="pm-icon-btn" onClick={onClose} title="Cerrar">✕</button>
        </div>
        <div className="pm-modal__body">{children}</div>
      </div>
    </div>
  );
}

/* Switch toggle reutilizable */
function SwitchToggle({ checked, onChange }) {
  return (
    <label className="switch" style={{ flexShrink: 0 }}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="slider" />
    </label>
  );
}

/* --------------------- Helpers --------------------- */
const ENVOLTORIOS_DEFAULT = ["Nori", "Sésamo", "Panko", "Ciboulette", "Masago", "Queso"];
const isRollCategoria = (cat) => (cat || "").toLowerCase().includes("roll");

/* --------------------- Plantilla CSV (delimitador ;) --------------------- */
const descargarPlantillaCSV = () => {
  const BOM = "\uFEFF";
  const rows = [
    ["nombre", "precio", "categoria", "activo"],
    ["Hosomaki Camarón", "4000", "Página 1", "true"]
  ];
  const csv = rows.map(r => r.map(v => {
    const needsQuotes = /[;"\n\r,]/.test(v);
    const safe = String(v).replace(/"/g, '""');
    return needsQuotes ? `"${safe}"` : safe;
  }).join(";")).join("\r\n");

  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "plantilla_productos.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/* --------------------- Formulario de Producto --------------------- */
const emptyProduct = { id: null, nombre: "", precio: "", categoria: "", activo: true, envolturas: [], stock: 0, control_stock: false };

function ProductForm({ initial = emptyProduct, onCancel, onSave, saving }) {
  const [form, setForm] = useState(initial);
  const roll = isRollCategoria(form.categoria);

  // switch local para habilitar/deshabilitar envolturas
  const [wrapsEnabled, setWrapsEnabled] = useState(
    Array.isArray(initial.envolturas) && initial.envolturas.length > 0
  );

  useEffect(() => {
    setForm({
      ...emptyProduct,
      ...initial,
      envolturas: Array.isArray(initial.envolturas) ? initial.envolturas : []
    });
    setWrapsEnabled(Array.isArray(initial.envolturas) && initial.envolturas.length > 0);
  }, [initial]);


  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast("El nombre es obligatorio");
    const precio = Number(form.precio);
    if (Number.isNaN(precio) || precio < 0) return toast("Precio inválido");
    const stock = Math.max(0, Number(form.stock ?? 0) || 0);

    const payload = { ...form, precio, stock };

    // reglas de envío para envolturas
    if (!roll || !wrapsEnabled) {
      payload.envolturas = []; // deshabilitadas
    } else {
      payload.envolturas = Array.isArray(form.envolturas) ? form.envolturas : [];
    }

    onSave(payload);
  };

  const toggleWrap = (w) => {
    setForm((f) => {
      const has = (f.envolturas || []).includes(w);
      const next = has ? f.envolturas.filter(x => x !== w) : [...(f.envolturas || []), w];
      return { ...f, envolturas: next };
    });
  };

  const addCustomWrap = (val) => {
    const t = (val || "").trim();
    if (!t) return;
    setForm((f) => {
      const curr = Array.isArray(f.envolturas) ? f.envolturas : [];
      if (curr.includes(t)) return f;
      return { ...f, envolturas: [...curr, t] };
    });
  };

  const handleWrapsEnabled = (checked) => {
    setWrapsEnabled(checked);
    if (!checked) {
      // si lo deshabilitan, limpiamos la lista
      setForm((f) => ({ ...f, envolturas: [] }));
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Sección: datos básicos */}
      <div className="pm-form-section">
        <div className="pm-form-section__head">
          <div className="pm-form-section__icon">🍣</div>
          <h4 className="pm-form-section__title">Datos del producto</h4>
        </div>

        <div className="pm-field">
          <label>Nombre</label>
          <input
            name="nombre"
            value={form.nombre}
            onChange={onChange}
            placeholder="Ej: California Roll"
            maxLength={120}
            required
          />
        </div>

        <div className="pm-form-row">
          <div className="pm-field">
            <label>Precio</label>
            <input
              name="precio"
              inputMode="numeric"
              value={form.precio}
              onChange={onChange}
              placeholder="Ej: 7490"
              required
            />
          </div>
          <div className="pm-field">
            <label>Categoría</label>
            <input
              name="categoria"
              value={form.categoria}
              onChange={onChange}
              placeholder="Ej: Rolls, Bebidas…"
              maxLength={60}
            />
          </div>
          <div className="pm-field">
            <label>Stock disponible</label>
            <input
              name="stock"
              inputMode="numeric"
              value={form.stock}
              onChange={onChange}
              placeholder="Ej: 50"
              min="0"
            />
          </div>
        </div>

        <div className="pm-toggle-row">
          <span className="pm-toggle-row__label">Producto activo</span>
          <SwitchToggle
            checked={!!form.activo}
            onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))}
          />
        </div>

        <div className="pm-toggle-row">
          <span className="pm-toggle-row__label">Controlar stock</span>
          <SwitchToggle
            checked={!!form.control_stock}
            onChange={(e) => setForm((f) => ({ ...f, control_stock: e.target.checked }))}
          />
        </div>
      </div>

      {/* Sección: envolturas */}
      <div className="pm-form-section">
          <div className="pm-form-section__head">
            <div className="pm-form-section__icon">🌯</div>
            <h4 className="pm-form-section__title">Envolturas</h4>
          </div>

          <div className="pm-wraps">
            <label className="pm-wraps__toggle">
              <input
                type="checkbox"
                checked={wrapsEnabled}
                onChange={(e) => handleWrapsEnabled(e.target.checked)}
              />
              Habilitar envolturas
            </label>

            <fieldset disabled={!wrapsEnabled} className="pm-wraps__fieldset">
              <div className="pm-wraps__head">Opciones disponibles</div>
              <div className="pm-wraps__grid">
                {ENVOLTORIOS_DEFAULT.map((w) => (
                  <label key={w} className="pm-chip">
                    <input
                      type="checkbox"
                      checked={(form.envolturas || []).includes(w)}
                      onChange={() => toggleWrap(w)}
                    />
                    <span>{w}</span>
                  </label>
                ))}
              </div>

              <div className="pm-wraps__add">
                <input
                  placeholder="Envoltura personalizada (Enter para agregar)…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomWrap(e.currentTarget.value);
                      e.currentTarget.value = "";
                    }
                  }}
                />
                {!!(form.envolturas || []).length && (
                  <div className="pm-wraps__selected">
                    {(form.envolturas || []).map((w) => (
                      <span key={w} className="pm-tag" onClick={() => toggleWrap(w)} title="Quitar">
                        {w} ×
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </fieldset>

            {!wrapsEnabled && (
              <p className="pm-wraps__hint pm-muted">Las envolturas están deshabilitadas.</p>
            )}
          </div>
        </div>

      {/* Acciones */}
      <div className="pm-actions">
        <button type="button" className="pm-btn" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="pm-btn pm-btn--accent" disabled={saving}>
          {saving ? "Guardando…" : form.id ? "Guardar cambios" : "Crear producto"}
        </button>
      </div>
    </form>
  );
}

/* --------------------- Página --------------------- */
export default function ProductManager() {
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState("Todas");
  const [soloActivos, setSoloActivos] = useState("Todos");

  // Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    try {
      setCargando(true);
      const { data } = await api.get("/api/productos");
      setProductos((data || []).map((p) => ({
        ...p,
        precio: Number(p.precio),
        categoria: p.categoria || "",
        envolturas: Array.isArray(p.envolturas) ? p.envolturas : [],
        stock: Number(p.stock ?? 0),
        control_stock: !!p.control_stock,
      })));
      setError("");
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Error");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);
  useEffect(() => { setPage(1); }, [busqueda, categoria, soloActivos]);

  const categorias = useMemo(() => {
    const set = new Set();
    for (const p of productos) if (p.categoria) set.add(p.categoria);
    return ["Todas", ...Array.from(set).sort()];
  }, [productos]);

  const listaFiltrada = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return productos
      .filter((p) => (categoria === "Todas" ? true : (p.categoria || "") === categoria))
      .filter((p) => (soloActivos === "Activos" ? p.activo : soloActivos === "Inactivos" ? !p.activo : true))
      .filter((p) => {
        if (!q) return true;
        return p.nombre.toLowerCase().includes(q) || (p.categoria || "").toLowerCase().includes(q);
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos, busqueda, categoria, soloActivos]);

  const total = listaFiltrada.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);
  const startIndex = (page - 1) * pageSize;
  const pageItems = listaFiltrada.slice(startIndex, startIndex + pageSize);

  /* -------- CRUD -------- */
  const abrirNuevo = () => { setEditando({ ...emptyProduct }); setModalOpen(true); };
  const abrirEditar = (p) => { setEditando({ ...p }); setModalOpen(true); };

  const guardar = async (form) => {
    try {
      setSaving(true);
      const payload = {
        nombre: form.nombre,
        precio: Number(form.precio),
        categoria: form.categoria,
        activo: !!form.activo,
        stock: Math.max(0, Number(form.stock ?? 0)),
        control_stock: !!form.control_stock,
        // si es roll y wraps habilitadas, se envían; si no, arreglo vacío
        envolturas: isRollCategoria(form.categoria)
          ? (Array.isArray(form.envolturas) ? form.envolturas : [])
          : []
      };
      if (form.id) await api.put(`/api/productos/${form.id}`, payload);
      else await api.post("/api/productos", payload);
      setModalOpen(false);
      await cargar();
    } catch (e) {
      toast(e?.response?.data?.error || e.message || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const cambiarActivo = async (producto) => {
    const activo = !producto.activo;
    try {
      await api.patch(`/api/productos/${producto.id}/activo`, { activo });
      setProductos((prev) => prev.map((p) => (
        p.id === producto.id ? { ...p, activo } : p
      )));
      toast(activo ? "Producto activado" : "Producto desactivado", "exito");
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo cambiar el estado");
    }
  };

  const eliminarProducto = async (producto) => {
    const ok = window.confirm(`Eliminar "${producto.nombre}"? Esta accion no se puede deshacer.`);
    if (!ok) return;
    try {
      await api.delete(`/api/productos/${producto.id}`);
      setProductos((prev) => prev.filter((p) => p.id !== producto.id));
      toast("Producto eliminado", "exito");
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo eliminar el producto");
    }
  };

  /* -------- Importar CSV -------- */
  const importarCSV = async (file) => {
    const textRaw = await file.text();
    const text = textRaw.replace(/^\uFEFF/, "");
    const firstLine = (text.split(/\r?\n/)[0] || "");
    const counts = {
      ";": (firstLine.match(/;/g) || []).length,
      ",": (firstLine.match(/,/g) || []).length,
      "\t": (firstLine.match(/\t/g) || []).length,
    };
    const delimiter = Object.entries(counts).sort((a,b)=>b[1]-a[1])[0][0] || ";";

    const parseCSV = (input, delim) => {
      const out = []; let row = []; let cur = ""; let inQuotes = false;
      const pushCell = () => { row.push(cur); cur = ""; };
      const pushRow  = () => { out.push(row); row = []; };
      for (let i = 0; i < input.length; i++) {
        const ch = input[i];
        if (inQuotes) {
          if (ch === '"') {
            if (input[i + 1] === '"') { cur += '"'; i++; } else { inQuotes = false; }
          } else { cur += ch; }
        } else {
          if (ch === '"') inQuotes = true;
          else if (ch === delim) pushCell();
          else if (ch === "\n") { pushCell(); pushRow(); }
          else if (ch === "\r") { /* ignore */ }
          else cur += ch;
        }
      }
      pushCell();
      if (row.length > 1 || (row.length === 1 && row[0].trim() !== "")) pushRow();
      return out;
    };

    const rows = parseCSV(text, delimiter).filter(r => r.some(c => String(c).trim() !== ""));
    if (!rows.length) return toast("CSV vacío");

    const headers = rows[0].map(h => String(h).trim().toLowerCase());
    const idx = {
      nombre: headers.indexOf("nombre"),
      precio: headers.indexOf("precio"),
      categoria: headers.indexOf("categoria"),
      activo: headers.indexOf("activo"),
    };
    if (idx.nombre < 0 || idx.precio < 0 || idx.categoria < 0 || idx.activo < 0) {
      return toast(`Encabezados requeridos: nombre;precio;categoria;activo`);
    }

    const items = [];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      const nombre = String(r[idx.nombre] ?? "").trim();
      const precioStr = String(r[idx.precio] ?? "").trim().replace(/\./g, "").replace(",", ".");
      const categoria = String(r[idx.categoria] ?? "").trim();
      const activoStr = String(r[idx.activo] ?? "").trim().toLowerCase();
      if (!nombre) continue;

      const precio = Number(precioStr);
      if (!Number.isFinite(precio) || precio < 0) {
        return toast(`Fila ${i + 1}: precio inválido (“${precioStr}”).`);
      }
      const activo = (activoStr === "true" || activoStr === "si" || activoStr === "sí" || activoStr === "1");
      items.push({ nombre, precio, categoria, activo });
    }

    if (!items.length) return toast("No hay filas válidas para importar.");
    if (!window.confirm(`Importar ${items.length} productos?`)) return;

    try {
      await api.post("/api/productos/bulk", { items });
      await cargar();
      toast("Importación completa", "exito");
    } catch (e) {
      toast(e?.response?.data?.error || "Error importando");
    }
  };

  /* -------- Paginador -------- */
  const PageButton = ({ disabled, active, onClick, children, title }) => (
    <button
      className={`pm-pagebtn ${active ? "active" : ""}`}
      disabled={disabled}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );

  const numericButtons = () => {
    const buttons = [];
    const maxToShow = 5;
    let start = Math.max(1, page - Math.floor(maxToShow / 2));
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, Math.min(start, end - maxToShow + 1));
    if (start > 1) {
      buttons.push(<PageButton key={1} onClick={() => setPage(1)}>1</PageButton>);
      if (start > 2) buttons.push(<span key="el1" className="pm-ellipsis">…</span>);
    }
    for (let i = start; i <= end; i++) {
      buttons.push(
        <PageButton key={i} active={i === page} onClick={() => setPage(i)}>
          {i}
        </PageButton>
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1) buttons.push(<span key="el2" className="pm-ellipsis">…</span>);
      buttons.push(<PageButton key={totalPages} onClick={() => setPage(totalPages)}>{totalPages}</PageButton>);
    }
    return buttons;
  };

  return (
    <div className="pm-page">
      <div className="pm-header">
        <div>
          <h1>Mantenedor de Productos</h1>
          <p className="pm-muted">Gestiona el menú: crea, edita, activa o elimina.</p>
        </div>
        <div className="pm-header__actions">
          <button className="pm-btn" onClick={descargarPlantillaCSV}>
            Descargar plantilla
          </button>

          <label className="pm-upload">
            <input
              type="file"
              accept=".csv"
              onChange={(e) => e.target.files?.[0] && importarCSV(e.target.files[0])}
            />
            Cargar Plantilla
          </label>

          <button className="pm-btn pm-btn--accent" onClick={abrirNuevo}>+ Nuevo</button>
        </div>
      </div>

      <div className="pm-toolbar">
        <div className="pm-search">
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre o categoría…"
          />
        </div>

        <div className="pm-filters">
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

        <select value={soloActivos} onChange={(e) => setSoloActivos(e.target.value)}>
            {["Todos", "Activos", "Inactivos"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <div className="pm-pagesize">
            <span>Items:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="pm-alert pm-alert--error">Error: {error}</div>}

      {cargando ? (
        <div className="pm-skel">Cargando productos…</div>
      ) : total === 0 ? (
        <div className="pm-empty">No hay productos que coincidan.</div>
      ) : (
        <>
          <div className="pm-table">
            <div className="pm-thead">
              <div>Nombre</div>
              <div className="pm-num">Precio</div>
              <div>Categoría</div>
              <div className="pm-num">Stock</div>
              <div className="pm-center">Control</div>
              <div>Envolturas</div>
              <div className="pm-center">Activo</div>
              <div className="pm-right">Acción</div>
            </div>

            <div className="pm-tbody">
              {pageItems.map((p) => (
                <div className="pm-row" key={p.id}>
                  <div className="pm-name">{p.nombre}</div>
                  <div className="pm-num">{fmtCLP(p.precio)}</div>
                  <div>{p.categoria || "—"}</div>
                  <div className="pm-num">{p.stock ?? 0}</div>
                  <div className="pm-center">
                    {p.control_stock ? <span title="Control de stock habilitado">✓</span> : "—"}
                  </div>
                  <div className="pm-wraps-col">
                    {Array.isArray(p.envolturas) && p.envolturas.length
                      ? p.envolturas.join(", ")
                      : "—"}
                  </div>
                  <div className="pm-center">
                    <button
                      type="button"
                      className={`pm-status-btn ${p.activo ? "yes" : "no"}`}
                      onClick={() => cambiarActivo(p)}
                      title={p.activo ? "Desactivar producto" : "Activar producto"}
                    >
                      {p.activo ? "Desactivar" : "Activar"}
                    </button>
                  </div>
                  <div className="pm-right">
                    <button className="pm-btn" onClick={() => abrirEditar(p)}>
                      Editar
                    </button>
                    <button className="pm-btn pm-btn--danger" onClick={() => eliminarProducto(p)}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pm-pager">
            <div className="pm-pager__info">
              {`Mostrando ${startIndex + 1}–${Math.min(startIndex + pageSize, total)} de ${total}`}
            </div>
            <div className="pm-pager__controls">
              <PageButton disabled={page === 1} onClick={() => setPage(1)} title="Primera">«</PageButton>
              <PageButton disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title="Anterior">‹</PageButton>
              {numericButtons()}
              <PageButton disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} title="Siguiente">›</PageButton>
              <PageButton disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Última">»</PageButton>
            </div>
          </div>
        </>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editando?.id ? "✏️ Editar producto" : "➕ Nuevo producto"}
      >
        <ProductForm
          initial={editando || emptyProduct}
          onCancel={() => setModalOpen(false)}
          onSave={guardar}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
